import { getApiErrorMessage } from '@/lib/api/error';
import { usePhoneVerification } from '@/hooks/usePhoneVerification';
import type { BookingAuthStep } from '@/types/booking-flow';
import type { AuthLoginResponseDto, SignInUserDto } from '@/types/dto/auth.dto';
import type { RequestCodeMethod } from '@/types/dto/phone-auth.dto';
import { useRef, useState } from 'react';
import { toast } from 'sonner';

interface UseBookingPhoneVerificationParams {
  onAuthStepChange: (step: BookingAuthStep) => void;
  onLinkedUserValidated: (user: AuthLoginResponseDto['user']) => Promise<void>;
  onProfileCompletionRequired: (user: AuthLoginResponseDto['user']) => void;
  onVerificationTokenReceived: (token: string) => void;
  onVerificationSignIn: (data: Pick<SignInUserDto, 'verificationToken'>) => Promise<AuthLoginResponseDto>;
  onVerificationCodeChange: (value: string) => void;
  phoneNumber: string;
  verificationCode: string;
}

export function useBookingPhoneVerification({
  onAuthStepChange,
  onLinkedUserValidated,
  onProfileCompletionRequired,
  onVerificationTokenReceived,
  onVerificationSignIn,
  onVerificationCodeChange,
  phoneNumber,
  verificationCode,
}: UseBookingPhoneVerificationParams) {
  const validationInFlight = useRef(false);
  const [isCompletingVerification, setIsCompletingVerification] = useState(false);
  const phoneVerification = usePhoneVerification({
    phoneNumber,
    verificationCode,
  });

  const requestCode = async (method: RequestCodeMethod) => {
    const hasRequestedCode = await phoneVerification.requestCode(method);

    if (hasRequestedCode) {
      onVerificationCodeChange('');
      onAuthStepChange('code');
    }
  };

  const validateCode = async () => {
    if (validationInFlight.current) return;
    validationInFlight.current = true;
    setIsCompletingVerification(true);

    try {
      const response = await phoneVerification.validateCode();
      if (!response) return;

      onVerificationTokenReceived(response.verificationToken);
      onVerificationCodeChange(verificationCode.replace(/\D/g, ''));
      const signInResponse = await onVerificationSignIn({ verificationToken: response.verificationToken });
      const mustCompleteProfile =
        !response.hasLinkedUser ||
        Boolean(response.requiresProfileCompletion) ||
        signInResponse.requiresProfileCompletion;

      if (mustCompleteProfile) {
        onProfileCompletionRequired(signInResponse.user);
        onAuthStepChange('name');
      } else {
        await onLinkedUserValidated(signInResponse.user);
      }
    } catch (error) {
      toast.error('Não foi possível validar o código', {
        description: getApiErrorMessage(error, 'Confira o código recebido e tente novamente.'),
      });
    } finally {
      validationInFlight.current = false;
      setIsCompletingVerification(false);
    }
  };

  return {
    isRequestingCode: phoneVerification.isRequestingCode,
    isValidatingCode: phoneVerification.isValidatingCode || isCompletingVerification,
    requestCode,
    requestedMethod: phoneVerification.requestedMethod,
    validateCode,
  };
}
